import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { margin } from 'polished';

import { EQUIPMENTS_URI } from 'constants/routes-constants';

import { ModalContext } from 'contexts/ModalContext';

import { Loading } from 'components/Loading';
import { OrdersSchedule } from 'pages/Totem/TotemDashboard/OrdersSchedule';
import { OrdersSmallHistory } from 'pages/Totem/TotemDashboard/OrdersSmallHistory';
import { OrderSelectionPopup } from 'pages/Totem/TotemDashboard/OrderSelectionPopup';
import { DeliveryInfoPopup } from 'pages/Totem/TotemDashboard/DeliveryInfoPopup';
import { DashboardHeader } from 'pages/Totem/TotemDashboard/DashboardHeader';
import { BudgetCard } from 'pages/Totem/TotemDashboard/Budget/BudgetCard';

import { DashboardWrapper, TotemCard, DashboardTitle, TotemLoadingStyle } from 'styles/pages/totem.styles';

import { TotemButton } from 'ui/Button';

import { analyticsEvents, analyticsPropertiesConstants, track } from 'tracking/segment';
import { DashboardOrderHistoryProps } from 'pages/Totem/TotemDashboard/TotemDashboard.types';
import { useGetReadyPunctualOrdersQuery } from 'data/__generated__';

export const TotemDashboard: React.FC<DashboardOrderHistoryProps> = ({
    baseUrl,
    currentTotem,
    defaultCategoryId,
    ordersOfTotem,
}) => {
    const { modal, isModalOpen } = useContext(ModalContext);
    const totemId = currentTotem?._id;
    const hasConfirmedTotemOrders = ordersOfTotem?.hasConfirmedTotemOrders;
    // Historique et message de section vide se basent sur le totem, pas sur `hasConfirmedTotemOrders` :
    // ce dernier se déduit des commandes ACTIVES (et de leurs occurrences précédentes) et retombe à faux
    // dès qu'il n'en reste aucune — un client dont toutes les commandes sont livrées/supprimées était
    // alors traité comme un nouveau client, et son historique disparaissait. `hasValidatedOrders` lit le
    // `stateHistory` (Validated/Ready déjà atteint), donc reste vrai une fois les commandes archivées.
    const hasValidatedOrders = !!currentTotem?.hasValidatedOrders;
    const [orderPopupOpen, setOrderPopupOpen] = useState(false);
    const [isDeliveryInfoOpen, setDeliveryInfoOpen] = useState(false);

    const {
        data: readyPunctualOrdersData,
        loading: readyPunctualOrdersLoading,
        error: readyPunctualOrdersError,
    } = useGetReadyPunctualOrdersQuery({ variables: { totemId }, skip: !hasConfirmedTotemOrders || !totemId });
    if (readyPunctualOrdersLoading) {
        return (
            <TotemLoadingStyle>
                <Loading />
            </TotemLoadingStyle>
        );
    }

    if (readyPunctualOrdersError) {
        throw Error(readyPunctualOrdersError?.message);
    }

    const readyPunctualOrders = readyPunctualOrdersData?.readyPunctualOrders || [];

    return (
        <DashboardWrapper>
            <DashboardHeader
                totem={currentTotem}
                openDeliveryInfoPopup={() => {
                    track(analyticsEvents.OPEN_TOTEM_DELIVERY_INFO);
                    setDeliveryInfoOpen(true);
                }}
            />
            <BudgetCard totemId={currentTotem?._id} totemMonthlyBudget={currentTotem?.monthlyBudget} />
            <TotemCard $marginMultiplier={[1.5, 3, 1.5, 3]}>
                <CardLine style={{ flexWrap: 'wrap' }}>
                    <DashboardTitle $marginMultiplier={[0, 1.5, 1, 0]}>Mes commandes en cours</DashboardTitle>
                    <TotemButton
                        $alignSelf="flex-start"
                        onClick={() => {
                            track(analyticsEvents.START_ORDER_CREATION, {
                                [analyticsPropertiesConstants.COMMON.IS_ONBOARDING]: !hasConfirmedTotemOrders,
                            });
                            setOrderPopupOpen(true);
                        }}
                        data-test="create-order-button"
                    >
                        Créer une commande
                    </TotemButton>
                </CardLine>
                <OrdersSchedule
                    baseUrl={baseUrl}
                    defaultCategoryId={defaultCategoryId}
                    hasValidatedOrders={hasValidatedOrders}
                    ordersOfTotem={ordersOfTotem}
                    readyPunctualOrders={readyPunctualOrders}
                />
                {hasValidatedOrders && (
                    <HistoryContainer>
                        <CardLine>
                            <DashboardTitle $marginMultiplier={[2, 1.5, 1, 0]}>
                                Mon historique de commandes
                            </DashboardTitle>
                        </CardLine>
                        <OrdersSmallHistory
                            totemId={totemId}
                            baseUrl={baseUrl}
                            defaultCategoryId={defaultCategoryId}
                            ordersOfTotem={ordersOfTotem}
                        />
                    </HistoryContainer>
                )}
            </TotemCard>
            <TotemCard $marginMultiplier={[1.5, 3, 1.5, 3]}>
                <CardLine>
                    <DashboardTitle $marginMultiplier={[0, 1.5, 0, 0]}>Mes équipements</DashboardTitle>
                    <TotemButton as={Link} $type="secondary" to={`${baseUrl}/${EQUIPMENTS_URI}`}>
                        Voir les équipements
                    </TotemButton>
                </CardLine>
                <DashboardText $marginMultiplier={[1.5, 0, 0, 0]}>
                    TOTEM vous propose en location ses meubles, machines à café, réfrigérateurs ...
                </DashboardText>
            </TotemCard>
            <OrderSelectionPopup
                orderPopupOpen={orderPopupOpen}
                closeOrderPopup={() => setOrderPopupOpen(false)}
                ordersOfTotem={ordersOfTotem}
                currentTotem={currentTotem}
                defaultCategoryId={defaultCategoryId}
            />
            {isDeliveryInfoOpen && (
                <DeliveryInfoPopup
                    currentTotem={currentTotem}
                    closeAddressPopup={() => setDeliveryInfoOpen(false)}
                    hasInstructions={true}
                />
            )}
            {isModalOpen && modal}
        </DashboardWrapper>
    );
};

const CardLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;
type DashboardTextProps = {
    color?: 'primary' | 'secondary' | 'black' | 'white' | 'success' | 'warning' | 'error';
    $bold?: boolean;
    $marginMultiplier?: number[];
};
const DashboardText = styled.span<DashboardTextProps>`
    display: inline-flex;
    align-items: center;
    color: ${({ color, theme: { colors } }) => colors[color || 'secondary']};
    font-size: 1.2em;
    ${({ $bold }) => $bold && 'font-weight: 800'};
    ${({
        theme: {
            variables: { margin: marg },
        },
        $marginMultiplier = [],
    }) =>
        margin(
            marg * ($marginMultiplier[0] || 0),
            marg * ($marginMultiplier[1] || 0),
            marg * ($marginMultiplier[2] || 0),
            marg * ($marginMultiplier[3] || 0),
        )};
`;

const HistoryContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
`;
